'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Region {
  id: number;
  name: string;
  countries: number;
  facilities: number;
  description: string;
}

interface GlobalReachProps {
  className?: string;
}

const GlobalReach = ({ className = '' }: GlobalReachProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<number>(1);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const regions: Region[] = [
    {
      id: 1,
      name: 'North America',
      countries: 3,
      facilities: 2500,
      description: 'Comprehensive coverage across the United States, Canada, and Mexico with regional distribution centers ensuring rapid delivery and local technical support.',
    },
    {
      id: 2,
      name: 'Europe',
      countries: 28,
      facilities: 1800,
      description: 'Strategic partnerships with authorized dealers across the European Union, providing CE-certified equipment and multilingual support services.',
    },
    {
      id: 3,
      name: 'Asia Pacific',
      countries: 15,
      facilities: 1200,
      description: 'Growing presence in key markets including Japan, South Korea, Singapore, and Australia with specialized equipment for regional healthcare needs.',
    },
    {
      id: 4,
      name: 'Middle East & Africa',
      countries: 12,
      facilities: 600,
      description: 'Expanding network serving major healthcare hubs in UAE, Saudi Arabia, South Africa, and Kenya with focus on emerging medical infrastructure.',
    },
  ];

  if (!isHydrated) {
    return (
      <section className={`py-20 bg-white ${className}`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Global Distribution Network
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              AIVA's international presence ensures reliable equipment delivery and support across six continents, serving healthcare facilities in over 50 countries.
            </p>
          </div>
          <div className="h-96 bg-surface rounded-2xl animate-pulse"></div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-20 bg-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Global Distribution Network
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            AIVA's international presence ensures reliable equipment delivery and support across six continents, serving healthcare facilities in over 50 countries.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="relative bg-surface rounded-2xl p-8 h-[500px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title="AIVA Global Distribution Centers"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps?q=40.7128,-74.0060&z=2&output=embed"
                className="rounded-2xl"
              ></iframe>
            </div>
            <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-brand">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="MapPinIcon" size={20} className="text-primary" />
                  <span className="text-sm font-semibold text-text-primary">
                    Distribution Centers Worldwide
                  </span>
                </div>
                <span className="text-xs text-text-secondary">Interactive Map</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {regions.map((region) => (
              <div
                key={region.id}
                onClick={() => setSelectedRegion(region.id)}
                className={`cursor-pointer rounded-xl p-6 transition-all duration-300 border-2 ${
                  selectedRegion === region.id
                    ? 'bg-primary/5 border-primary shadow-brand'
                    : 'bg-white border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-xl font-bold text-text-primary">{region.name}</h3>
                  <Icon
                    name="ChevronRightIcon"
                    size={24}
                    className={`transition-transform duration-300 ${
                      selectedRegion === region.id ? 'rotate-90 text-primary' : 'text-text-secondary'
                    }`}
                  />
                </div>
                <div className="flex items-center space-x-6 mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="GlobeAltIcon" size={16} className="text-accent" />
                    <span className="text-sm text-text-secondary">
                      {region.countries} Countries
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="BuildingOfficeIcon" size={16} className="text-brand-accent" />
                    <span className="text-sm text-text-secondary">
                      {region.facilities.toLocaleString()} Facilities
                    </span>
                  </div>
                </div>
                {selectedRegion === region.id && (
                  <p className="text-sm text-text-secondary leading-relaxed animate-fade-in">
                    {region.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-primary to-accent rounded-xl p-6 text-white text-center">
            <Icon name="TruckIcon" size={32} className="mx-auto mb-3 opacity-90" />
            <p className="text-3xl font-bold mb-1">50+</p>
            <p className="text-sm opacity-80">Countries Served</p>
          </div>
          <div className="bg-gradient-to-br from-brand-accent to-green-500 rounded-xl p-6 text-white text-center">
            <Icon name="BuildingStorefrontIcon" size={32} className="mx-auto mb-3 opacity-90" />
            <p className="text-3xl font-bold mb-1">6,100+</p>
            <p className="text-sm opacity-80">Healthcare Facilities</p>
          </div>
          <div className="bg-gradient-to-br from-accent to-blue-400 rounded-xl p-6 text-white text-center">
            <Icon name="UserGroupIcon" size={32} className="mx-auto mb-3 opacity-90" />
            <p className="text-3xl font-bold mb-1">200+</p>
            <p className="text-sm opacity-80">Authorized Dealers</p>
          </div>
          <div className="bg-gradient-to-br from-brand-cta to-red-500 rounded-xl p-6 text-white text-center">
            <Icon name="ClockIcon" size={32} className="mx-auto mb-3 opacity-90" />
            <p className="text-3xl font-bold mb-1">24/7</p>
            <p className="text-sm opacity-80">Global Support</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalReach;